import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    clearAppointmentAttendanceDetails,
    searchAppointmentAttendanceDetails
} from '../../../store/calendarService';
import { searchParticipants } from '../../../store/participantService';
import { clearUsersMinimalDetails, loadUsersMinimalDetails } from '../../../store/userService';
import { calcEndDate, calcStartDate } from '../../../utils/dateFunctions';
import { ATTENDANCE_OUTCOMES_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import AttendanceOutcomeTableRow from './AttendanceOutcomeTableRow';
import CreateAttendanceOutcomesCsv from './CreateAttendanceOutcomesCsv';

import app from '../../../app.module.css';

const headCells = [
    { id: 'service', label: 'Service', sortable: false },
    { id: 'adviser', label: 'Adviser' },
    { id: 'ptCode', label: 'PT ID' },
    { id: 'uniqueId', label: 'Unique ID' },
    { id: 'firstName', label: 'First Name' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'referralDate', label: 'Referral Date' },
    { id: 'currentStatus', label: 'Current Status' },
    { id: 'appointmentDate', label: 'Appointment Date' },
    { id: 'appointmentTime', label: 'Appointment Time' },
    { id: 'appointmentType', label: 'Appointment Type' },
    { id: 'interviewer', label: 'Interviewer' },
    { id: 'location', label: 'Location' },
    { id: 'appointmentAttendance', label: 'Appointment Attendance' },
    { id: 'poNumber', label: 'PO Number' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'ptCode',
    page: 0,
    rowsPerPage: 100
};

const _SEARCH_DAYS_BEFORE = 7;
const _SEARCH_DAYS_AFTER = 14;
const canGenerateRoles = [PRAP, QUALITY, SUPERUSER];

const AttendanceOutcome = () => {
    // HOOKS
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const initialSearchParams = {
        serviceIds: [],
        inactive: false,
        dateFrom: '',
        dateTo: '',
        statusIds: []
    };

    const [rows, setRows] = useState([]);
    const [searchParams, setSearchParams] = useState(initialSearchParams);
    const [participantIds, setParticipantIds] = useState([]);
    const [userIdsForInterviewer, setUserIdsForInterviewer] = useState([]);
    const [attendanceSearch, setAttendanceSearch] = useState([]);
    const [isAdvisers, setIsAdvisers] = useState(false);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDataSetsModalOpen, setIsDataSetsModalOpen] = useState(false);
    const [generateCSV, setGenerateCSV] = useState(false);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const userDetails = useSelector((state) => state.entities.userService.usersMinimalDetails);
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const appointmentAttendanceDetails = useSelector(
        (state) => state.entities.calendarService.appointmentAttendanceDetails
    );
    const appointmentAttendanceDetailsMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentAttendanceDetailsMetaData
    );
    const loadingAppointmentAttendanceDetails = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentAttendanceDetails
    );

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        if (!state) return;
        if (!('id' in state.selects.contract) || !('id' in state.selects.service)) return;

        const newSearchParams = {
            serviceIds: [state.selects.service.id],
            inactive: false,
            dateFrom: calcStartDate(_SEARCH_DAYS_BEFORE, state.selects.date),
            dateTo: calcEndDate(state.selects.date, _SEARCH_DAYS_AFTER),
            statusIds: state.selects.attendanceStatuses.map((el) => el.id)
        };
        setSearchParams(newSearchParams);
        dispatch(searchAppointmentAttendanceDetails(newSearchParams, 0));
    }, [state]);

    useEffect(() => {
        if (
            searchParams.statusIds.length &&
            appointmentAttendanceDetails?.length > 0 &&
            !loadingAppointmentAttendanceDetails &&
            'number' in appointmentAttendanceDetailsMetaData &&
            !appointmentAttendanceDetailsMetaData?.last
        ) {
            dispatch(
                searchAppointmentAttendanceDetails(
                    searchParams,
                    appointmentAttendanceDetailsMetaData.number + 1
                )
            );
        } else if (
            appointmentAttendanceDetailsMetaData.last &&
            appointmentAttendanceDetails?.length < 1
        ) {
            setLoadingContentMessage(ATTENDANCE_OUTCOMES_ERROR);
        } else if (
            appointmentAttendanceDetailsMetaData.last &&
            appointmentAttendanceDetails?.length > 0
        ) {
            setAttendanceSearch(appointmentAttendanceDetails);
            const ptIds = [...new Set(appointmentAttendanceDetails.map((el) => el.participantId))];
            if (ptIds.length > 0) {
                setParticipantIds(ptIds);
                dispatch(searchParticipants({ ids: ptIds }, 0));
            }
            const userIds = [...new Set(appointmentAttendanceDetails.map((el) => el.userId))];
            if (userIds.length) setUserIdsForInterviewer(userIds);
        }
    }, [appointmentAttendanceDetails, appointmentAttendanceDetailsMetaData, searchParams]);

    useEffect(() => {
        if (
            participantIds.length > 0 &&
            participantsSearch?.length > 0 &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(
                searchParticipants({ ids: participantIds }, participantsSearchMetaData.number + 1)
            );
        } else if (participantsSearchMetaData.last && participantsSearch?.length < 1) {
            setLoadingContentMessage(ATTENDANCE_OUTCOMES_ERROR);
        } else if (participantsSearchMetaData.last && participantsSearch?.length > 0) {
            const participantUserIds = [
                ...new Set(participantsSearch.map((el) => el.userId).filter((el) => el))
            ];
            if (participantUserIds.length || userIdsForInterviewer.length)
                dispatch(
                    loadUsersMinimalDetails([...participantUserIds, ...userIdsForInterviewer])
                );
            setIsAdvisers([...participantUserIds, ...userIdsForInterviewer].length > 0);
        }
    }, [participantsSearch, participantsSearchMetaData, participantIds]);

    useEffect(() => {
        if (
            participantsSearch?.length < 1 ||
            !participantsSearchMetaData?.last ||
            (isAdvisers && userDetails?.length < 1) ||
            attendanceSearch.length < 1 ||
            loadingAppointmentAttendanceDetails ||
            !appointmentAttendanceDetailsMetaData?.last
        )
            return;

        const searchRows = attendanceSearch
            .map((el) => {
                const participant =
                    participantsSearch.find((entry) => entry.id === el.participantId) || {};
                const adviser =
                    participant && participant?.userId
                        ? userDetails.find((entry) => entry.id === participant.userId) || {}
                        : {};
                const interviewer = el?.userId
                    ? userDetails.find((entry) => entry.id === el.userId) || {}
                    : {};
                const formattedAttendanceName = el.status.replace('–', 'by');
                return {
                    rowId: el.id,
                    service: state.selects.service.name,
                    adviser: `${adviser?.firstName || ''} ${adviser?.lastName || ''}`,
                    ptCode: participant?.ptCode || '',
                    uniqueId: participant?.uniqueIdentifier || '',
                    firstName: participant?.firstName || '',
                    lastName: participant?.lastName || '',
                    referralDate: participant?.referralDate || '',
                    currentStatus: participant?.status || '',
                    appointmentDate: el.date,
                    appointmentTime: el.startTime || '',
                    appointmentType: el?.type || '',
                    interviewer: `${interviewer?.firstName || ''} ${interviewer?.lastName || ''}`,
                    location: el?.location || '',
                    appointmentAttendance: formattedAttendanceName,
                    poNumber: participant?.poNumber || ''
                };
            })
            .filter((el) => el.ptCode);
        setRows(searchRows);
    }, [
        isAdvisers,
        userDetails,
        attendanceSearch,
        appointmentAttendanceDetailsMetaData,
        participantsSearch,
        participantsSearchMetaData
    ]);

    // HELPER FNS
    const loadingError = () => {
        if (!state) return 'No Data Set query found for Attendance Outcome';
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <AttendanceOutcomeTableRow key={el.rowId} row={el} />
        ));

    const cleanUp = () => {
        dispatch(clearAppointmentAttendanceDetails());
        dispatch(clearUsersMinimalDetails());
        setRows([]);
        setSearchParams(initialSearchParams);
        setAttendanceSearch([]);
        setIsAdvisers(false);
        setRowMetaData(initialRowMetaData);
        setGenerateCSV(false);
    };

    // EVENT HANDLERS
    const onDataSetsModalClose = () => {
        setIsDataSetsModalOpen(false);
    };

    const onGenerateCsv = () => {
        setGenerateCSV(true);
        setIsDataSetsModalOpen(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>
                Data Sets Results Screen - Attendance Outcome - {state.selects.contract.name}
            </h1>
            {rows?.length < 1 ? (
                <LoadingSpinner content={loadingContentMessage} />
            ) : (
                <>
                    <ResultsTable
                        defaultOrderBy={'ptCode'}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />

                    <Button
                        id="generateDataSets"
                        type="button"
                        disabled={!hasRole(canGenerateRoles, roles)}
                        content="Generate"
                        onClick={onGenerateCsv}
                    />
                </>
            )}
            {generateCSV && (
                <CreateAttendanceOutcomesCsv
                    selectedAttendanceStatuses={state.selects.attendanceStatuses}
                    isOpen={isDataSetsModalOpen}
                    onClose={onDataSetsModalClose}
                    rows={rows}
                    contractName={state.selects.contract.name}
                />
            )}
        </div>
    );
};

export default AttendanceOutcome;
